import { useEffect, useState } from "react";
import { useLazyGetImageDownloadUrlQuery } from "../../shared";
import { Avatar } from "../../shared";
import PropTypes from "prop-types";

/**
 * S3Image Component
 *
 * Wrapper component that automatically fetches signed URLs for private S3 images
 * Falls back to direct URL if the image is already public or if URL generation fails
 *
 * Usage:
 *   <S3Image src={user.profileImageUrl} alt="Profile" name="John Doe" size="lg" variant="avatar" />
 *   <S3Image src={post.imageUrl} alt="" variant="image" className="w-full h-auto" />
 */
const S3Image = ({
  src,
  alt = "",
  name,
  size = "md",
  variant = "avatar",
  className = "",
  ...props
}) => {
  const [displayUrl, setDisplayUrl] = useState(null);

  const [fetchSignedUrl, { data: signedUrlData, isLoading, isError }] =
    useLazyGetImageDownloadUrlQuery();

  useEffect(() => {
    // Reset state when src changes
    setDisplayUrl(null);

    if (!src) {
      return;
    }

    // Check if the URL is an S3 URL that needs signing
    const isS3Url = src.includes('.s3.') || src.includes('s3.amazonaws.com');

    if (isS3Url) {
      // Fetch signed URL for S3 images
      fetchSignedUrl(src);
    } else {
      // Use URL directly for non-S3 images (e.g., external URLs, data URLs)
      setDisplayUrl(src);
    }
  }, [src, fetchSignedUrl]);

  useEffect(() => {
    if (signedUrlData?.data?.signedUrl) {
      setDisplayUrl(signedUrlData.data.signedUrl);
    }
  }, [signedUrlData]);

  useEffect(() => {
    if (isError) {
      // If signed URL fetch fails, fall back to direct URL
      // This handles cases where the image might already be public
      setDisplayUrl(src);
    }
  }, [isError, src]);

  // AVATAR VARIANT - Use Avatar component for profile pictures
  if (variant === "avatar") {
    // If no src provided, render Avatar without image (will show initials or fallback)
    if (!src) {
      return (
        <Avatar
          src={null}
          alt={alt}
          name={name}
          size={size}
          className={className}
          {...props}
        />
      );
    }

    // Show loading state while fetching signed URL
    if (isLoading || (src && !displayUrl)) {
      return (
        <Avatar
          src={null}
          alt={alt}
          name={name}
          size={size}
          className={className}
          {...props}
        />
      );
    }

    // Render Avatar with the display URL (either signed or direct)
    return (
      <Avatar
        src={displayUrl}
        alt={alt}
        name={name}
        size={size}
        className={className}
        {...props}
      />
    );
  }

  // IMAGE VARIANT - Use regular img tag for post images, documents, etc.
  if (variant === "image") {
    // Show loading state while fetching signed URL
    if (isLoading || (src && !displayUrl)) {
      return (
        <div className={`bg-neutral-200 animate-pulse ${className}`} {...props}>
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            Loading...
          </div>
        </div>
      );
    }

    // Render regular img with the display URL (either signed or direct)
    return (
      <img
        src={displayUrl}
        alt={alt}
        className={className}
        onError={(e) => {
          e.target.style.display = 'none';
          console.error('Failed to load image:', src);
        }}
        {...props}
      />
    );
  }

  return null;
};

S3Image.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "2xl"]),
  variant: PropTypes.oneOf(["avatar", "image"]),
  className: PropTypes.string,
};

export default S3Image;
